import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertCircle,
  Brain,
  Building,
  CheckCircle,
  Clock,
  Copy,
  Download,
  FileIcon,
  FileText,
  Tag,
  Upload,
  X,
  XCircle,
  Zap
} from 'lucide-react'
import { useCallback, useContext, useState } from 'react'
import { ThemeContext } from '../contexts/ThemeContext'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'

const DocumentAnalysis = ({ currentUser }) => {
  const { isDarkMode: darkMode } = useContext(ThemeContext)
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([]) // 改為陣列支援多檔案
  const [analysisResults, setAnalysisResults] = useState([]) // 儲存所有分析結果
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, status: {} }) // 批次進度
  const [error, setError] = useState(null)
  const [fileInputKey, setFileInputKey] = useState(Date.now()) // 用於強制重新渲染文件輸入

  // 權限檢查函數
  const hasPermission = (requiredRoles) => {
    if (!currentUser || !requiredRoles) return false
    return requiredRoles.includes(currentUser.role)
  }

  // 檢查是否為開發人員或超級管理員（可以查看原始結果）
  const canViewRawResults = hasPermission(['super_admin', 'admin', 'developer']) ||
    (currentUser && currentUser.can_view_document_analysis_raw)

  // 支援的文件格式
  const supportedFormats = {
    documents: ['pdf', 'docx', 'doc', 'txt']
  }

  const maxFileSize = 50 * 1024 * 1024 // 50MB

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFilesSelect(files)
    }
  }, [])

  const handleFilesSelect = (files) => {
    // 完全重置所有相關狀態
    setError(null)
    setAnalysisResults([])
    setIsAnalyzing(false)
    setIsDragOver(false)

    const validFiles = []
    const errors = []
    const allFormats = supportedFormats.documents

    files.forEach(file => {
      // 檢查文件大小
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: 文件大小超過限制 (${maxFileSize / (1024 * 1024)}MB)`)
        return
      }

      // 檢查文件格式
      const fileExtension = file.name.split('.').pop().toLowerCase()
      if (!allFormats.includes(fileExtension)) {
        errors.push(`${file.name}: 不支援的格式`)
        return
      }

      validFiles.push(file)
    })

    if (errors.length > 0) {
      setError(errors.join('\n'))
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles)
    }
  }

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      // 清除之前的錯誤狀態
      setError(null)
      handleFilesSelect(files)
    }
  }

  const triggerFileInput = () => {
    // 確保在點擊選擇文件時清除之前的錯誤和狀態
    setError(null)
    setIsDragOver(false)

    // 確保 input 元素可以響應
    const fileInput = document.getElementById('file-upload')
    if (fileInput) {
      // 強制重置 input，確保 onChange 事件能觸發
      fileInput.value = ''
      fileInput.click()
    }
  }

  const analyzeBatchFiles = async () => {
    if (selectedFiles.length === 0) return

    setIsAnalyzing(true)
    setError(null)
    setBatchProgress({ current: 0, total: selectedFiles.length, status: {} })
    const results = []

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]

        // 更新進度 - 開始處理時顯示「正在處理第 i+1 個」，但 current 保持為已完成數量 i
        setBatchProgress(prev => ({
          ...prev,
          current: i,  // 已完成 i 個
          currentProcessing: i + 1,  // 正在處理第 i+1 個
          status: { ...prev.status, [file.name]: 'uploading' }
        }))

        try {
          const formData = new FormData()
          formData.append('file', file)

          const response = await fetch('/api/document/upload', {
            method: 'POST',
            body: formData
          })

          if (!response.ok) {
            throw new Error('分析失敗')
          }

          const result = await response.json()
          results.push({ file: file.name, success: true, data: result.data })

          // 更新狀態為成功，current 更新為已完成數量
          setBatchProgress(prev => ({
            ...prev,
            current: i + 1,  // 完成後更新為 i+1
            status: { ...prev.status, [file.name]: 'success' }
          }))

        } catch (err) {
          results.push({ file: file.name, success: false, error: err.message })

          // 更新狀態為失敗，current 仍然更新（處理完畢，不論成功失敗）
          setBatchProgress(prev => ({
            ...prev,
            current: i + 1,
            status: { ...prev.status, [file.name]: 'error' }
          }))
        }

        // 短暫延遲避免過快
        await new Promise(resolve => setTimeout(resolve, 300))
      }

      setAnalysisResults(results)

      // 不再使用 alert，改為依賴 UI 內的視覺回饋

    } catch (err) {
      setError(err.message)
      console.error('批次分析錯誤:', err)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  const downloadResult = () => {
    if (analysisResults.length === 0) return

    const dataStr = JSON.stringify(analysisResults, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `batch_analysis_result_${Date.now()}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const clearAll = () => {
    // 重置所有狀態
    setSelectedFiles([])
    setAnalysisResults([])
    setError(null)
    setIsAnalyzing(false)
    setIsDragOver(false)
    setBatchProgress({ current: 0, total: 0, status: {} })

    // 生成新的 key 來強制重新渲染文件輸入
    setFileInputKey(Date.now())

    // 清除文件 input 的值，確保可以重新選擇相同文件
    const fileInput = document.getElementById('file-upload')
    if (fileInput) {
      fileInput.value = ''
      // 強制移除和重新添加 input 以確保完全重置
      fileInput.blur()
    }

    // 強制重新渲染文件上傳區域
    setTimeout(() => {
      // 確保所有狀態都已更新

      // 額外確保 input 狀態清除
      const fileInput = document.getElementById('file-upload')
      if (fileInput) {
        fileInput.value = ''
        // 觸發一個 change 事件來確保清除
        const event = new Event('change', { bubbles: true })
        fileInput.dispatchEvent(event)
      }
    }, 100)
  }

  const renderFileInfo = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase()

    return (
      <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <FileText className="h-8 w-8 text-blue-500" />
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {(file.size / 1024 / 1024).toFixed(2)} MB • {fileExtension.toUpperCase()}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // 從 selectedFiles 中移除該檔案
            setSelectedFiles(prev => prev.filter(f => f.name !== file.name))
          }}
          className="text-gray-400 hover:text-red-500"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  // 渲染單個分析結果的詳細內容
  const renderSingleAnalysisDetail = (analysisData) => {
    if (!analysisData) return null

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {analysisData.filename && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FileIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">文件名稱</span>
                </div>
                <p className="text-sm font-medium truncate">{analysisData.filename}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Clock className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">分析時間</span>
              </div>
              <p className="text-sm font-medium">
                {new Date(analysisData.upload_time || analysisData.analysis_time || Date.now()).toLocaleString()}
              </p>
            </CardContent>
          </Card>

          {/* 置信度分數 */}
          {analysisData.overall_confidence && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">分析置信度</span>
                </div>
                <p className="text-2xl font-bold mb-2">
                  {Math.round(analysisData.overall_confidence * 100)}%
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {(() => {
                    const confidence = Math.round(analysisData.overall_confidence * 100);
                    if (confidence >= 80) {
                      return (
                        <div>
                          <div className="font-medium text-green-600 dark:text-green-400">高置信度</div>
                          <div>分析結果可靠，可直接用於決策</div>
                        </div>
                      );
                    } else if (confidence >= 60) {
                      return (
                        <div>
                          <div className="font-medium text-yellow-600 dark:text-yellow-400">中等置信度</div>
                          <div>結果有參考價值，建議人工複查</div>
                        </div>
                      );
                    } else {
                      return (
                        <div>
                          <div className="font-medium text-red-600 dark:text-red-400">低置信度</div>
                          <div>結果僅供參考，需要人工重新分析</div>
                        </div>
                      );
                    }
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 技術分類 */}
          {analysisData.invention_categorization && (
            <Card>
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Tag className="h-4 w-4 text-indigo-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">技術分類</span>
                </div>
                <p className="text-sm font-medium">{analysisData.invention_categorization}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 智能摘要 */}
        {analysisData.intelligent_summary && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center space-x-2">
                <Brain className="h-4 w-4 text-blue-500" />
                <span>AI 智能摘要</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-48 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm whitespace-pre-wrap break-words">{analysisData.intelligent_summary}</p>
              </div>
              <div className="flex justify-end mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(analysisData.intelligent_summary)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  複製摘要
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 創新分析 */}
        {analysisData.innovation_analysis && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center space-x-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                <span>技術創新分析</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-32 overflow-y-auto p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm whitespace-pre-wrap break-words">{analysisData.innovation_analysis}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 商業化潛力 */}
        {analysisData.commercialization_potential && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center space-x-2">
                <Building className="h-4 w-4 text-green-500" />
                <span>商業化潛力評估</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-32 overflow-y-auto p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm whitespace-pre-wrap break-words">{analysisData.commercialization_potential}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 技術關鍵詞 */}
        {analysisData.technical_keywords && analysisData.technical_keywords.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center space-x-2">
                <Tag className="h-4 w-4 text-purple-500" />
                <span>技術關鍵詞</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {analysisData.technical_keywords.slice(0, 15).map((keyword, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  // 渲染所有分析結果
  const renderAnalysisResults = () => {
    // 只有當有成功的分析結果時才渲染
    const successfulResults = analysisResults.filter(r => r.success && r.data)
    if (successfulResults.length === 0) return null

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 分析結果標題 */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            分析結果 ({successfulResults.length} 個文件)
          </h3>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={downloadResult}>
              <Download className="h-4 w-4 mr-2" />
              下載結果
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll}>
              <X className="h-4 w-4 mr-2" />
              清除
            </Button>
          </div>
        </div>

        {/* 為每個成功的結果創建標籤頁 */}
        <Tabs defaultValue={successfulResults[0]?.file || 'result-0'} className="space-y-4">
          <TabsList className="flex-wrap h-auto">
            {successfulResults.map((result, index) => (
              <TabsTrigger key={result.file || index} value={result.file || `result-${index}`} className="text-xs">
                <FileText className="h-3 w-3 mr-1" />
                {result.file?.length > 20 ? result.file.substring(0, 20) + '...' : result.file}
              </TabsTrigger>
            ))}
            {canViewRawResults && (
              <TabsTrigger value="raw-all">原始結果</TabsTrigger>
            )}
          </TabsList>

          {successfulResults.map((result, index) => (
            <TabsContent key={result.file || index} value={result.file || `result-${index}`} className="space-y-4">
              {renderSingleAnalysisDetail(result.data)}
            </TabsContent>
          ))}

          {canViewRawResults && (
            <TabsContent value="raw-all">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">原始分析結果</CardTitle>
                  <CardDescription>完整的JSON格式分析數據</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-80 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <pre className="text-xs whitespace-pre-wrap break-words">
                      {JSON.stringify(analysisResults, null, 2)}
                    </pre>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(JSON.stringify(analysisResults, null, 2))}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      複製JSON
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">文件分析</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">AI驅動的文件處理與分析系統</p>
        </div>
        <div className="flex items-center space-x-3">
          <Badge variant="outline" className="text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400">
            <Brain className="h-3 w-3 mr-1" />
            AI 分析
          </Badge>
          {analysisResults.length > 0 && (
            <Button variant="outline" size="sm" onClick={downloadResult}>
              <Download className="h-4 w-4 mr-2" />
              匯出結果
            </Button>
          )}
        </div>
      </motion.div>

      {/* 文件上傳卡片 */}
      <Card>
        <CardHeader>
          <CardTitle>文件上傳</CardTitle>
          <CardDescription>上傳文件進行智能分析</CardDescription>
        </CardHeader>
        <CardContent>
          {/* 文件上傳區域 - 使用網格佈局 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 左側：文件上傳區域 */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragOver
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                拖拽文件到此處，或點擊上傳
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                支援 PDF、Word、TXT 格式，最大 50MB
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-4">
                💡 可同時選擇多個文件進行批量上傳
              </p>
              <input
                key={fileInputKey}
                type="file"
                onChange={handleFileInputChange}
                accept=".pdf,.docx,.doc,.txt"
                className="hidden"
                id="file-upload"
                multiple
              />
              <Button onClick={triggerFileInput}>
                選擇文件
              </Button>
            </div>

            {/* 右側：選中的文件列表 */}
            {selectedFiles.length > 0 && analysisResults.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-4"
              >
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    已選擇 {selectedFiles.length} 個文件
                  </p>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      {batchProgress.status[file.name] && (
                        <div className="flex-shrink-0">
                          {batchProgress.status[file.name] === 'uploading' && (
                            <Clock className="h-4 w-4 text-yellow-500 animate-spin" />
                          )}
                          {batchProgress.status[file.name] === 'success' && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          {batchProgress.status[file.name] === 'error' && (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  {/* 批次進度 */}
                  {isAnalyzing && batchProgress.total > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          正在處理: {batchProgress.currentProcessing || 1}/{batchProgress.total}
                        </span>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {Math.round((batchProgress.current / batchProgress.total) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center gap-3">
                    <Button
                      onClick={analyzeBatchFiles}
                      disabled={isAnalyzing}
                      className="px-8"
                    >
                      {isAnalyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          批量分析中...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-2" />
                          開始批量分析 ({selectedFiles.length})
                        </>
                      )}
                    </Button>
                    {!isAnalyzing && (
                      <Button variant="outline" onClick={clearAll}>
                        清除全部
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* 批次分析結果摘要 */}
            {analysisResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="lg:col-span-2 space-y-3"
              >
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    批次分析完成
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">總計:</span>
                      <span className="ml-2 font-medium">{analysisResults.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">成功:</span>
                      <span className="ml-2 font-medium text-green-600">
                        {analysisResults.filter(r => r.success).length}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">失敗:</span>
                      <span className="ml-2 font-medium text-red-600">
                        {analysisResults.filter(r => !r.success).length}
                      </span>
                    </div>
                  </div>
                  <Button
                    className="mt-4 w-full"
                    onClick={clearAll}
                  >
                    重新上傳
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 錯誤訊息 */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700 dark:text-red-400">{error}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 分析結果 */}
      {renderAnalysisResults()}
    </div>
  )
}

export default DocumentAnalysis




